/**
 * @class MCCSearch
 * @extends HTMLElement
 * @classdesc Custom element that initializes and manages an iframe for MCC search functionality.
 *
 * @constructor
 * Creates an instance of MCCSearch and initializes the iframe.
 *
 * @method initIframe
 * Initializes an iframe and appends it to the document body if it doesn't already exist.
 * The iframe is configured to load from 'http://localhost:4001' and is styled to cover the entire viewport.
 * The iframe is initially hidden and has a high z-index to ensure it appears above other elements when displayed.
 */

if (!customElements.get('mcc-search')) {
    class MCCSearch extends HTMLElement {
        constructor() {
            super();
            this.iframeSrc =
                this.getAttribute('iframeSrc') || 'http://localhost:4001';
            this.initIframe();

            this.redirectUrl = this.getAttribute('redirect-url');
        }

        connectedCallback() {
            if (!this.iframe) {
                return;
            }

            window.addEventListener('message', this.handleMessage.bind(this));
        }

        disconnectedCallback() {
            if (!this.iframe) {
                return;
            }

            window.removeEventListener(
                'message',
                this.handleMessage.bind(this),
            );
        }

        /**
         * Handles messages received from an iframe.
         *
         * @param {MessageEvent} event - The message event received from the iframe.
         * @property {string} event.origin - The origin of the message event.
         * @property {Object} event.data - The data sent with the message event.
         * @property {string} event.data.type - The type of the message event.
         * @property {string} [event.data.color] - The selected color (if type is 'selectColor').
         * @property {string} [event.data.undercoat] - The selected undercoat (if type is 'selectColor').
         *
         * @returns {void}
         */
        handleMessage(event) {
            if (event.origin !== this.iframeSrc) return;

            const { type } = event.data;

            if (type === 'closeModal') {
                setTimeout(() => {
                    this.iframe.style.display = 'none';
                }, 500);
            }

            if (type === 'selectColor') {
                // // Quick hack to close the modal after selecting a color
                // setTimeout(() => {
                //     this.iframe.style.display = 'none';
                // }, 500);

                const { color, undercoat } = event.data;

                // Json encode and base 64 encode the color and undercoat
                const colorBase64 = btoa(JSON.stringify({color, undercoat}));

                if (this.redirectUrl) {
                    // Redirect window to the selected color page
                    window.location.href = this.buildUrl(colorBase64);
                }

                const selectedColorElement =
                    document.querySelector('mcc-selected-color');
                if (selectedColorElement) {
                    selectedColorElement.dispatchEvent(
                        new CustomEvent('colorSelected', {
                            detail: { color, undercoat },
                        }),
                    );
                }
            }
        }

        buildUrl(colorBase64) {
            /**
             * Add colorBase64 to the redirectUrl and return the new url
             */
            const url = new URL(this.redirectUrl);
            url.searchParams.set('color', colorBase64);
            return url.toString();
        }

        initIframe() {
            /**
             * Initializes an iframe and appends it to the document body if it doesn't already exist.
             * The iframe is configured to load from the provided iframeSrc attribute and is styled to cover the entire viewport.
             * The iframe is initially hidden and has a high z-index to ensure it appears above other elements when displayed.
             */
            if (!document.querySelector(`iframe[src="${this.iframeSrc}"]`)) {
                const iframe = document.createElement('iframe');

                iframe.src = this.iframeSrc;
                iframe.allow = 'fullscreen';

                iframe.style.position = 'fixed';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = 'none';
                iframe.style.zIndex = '9999';
                iframe.style.display = 'none';

                iframe.id = 'mcc-iframe';
                iframe.className = 'mcc-iframe';

                this.iframe = iframe;

                document.body.appendChild(iframe);
            }
        }
    }

    customElements.define('mcc-search', MCCSearch);
}

/**
 * @class MCCSearchButton
 * @extends HTMLElement
 * @classdesc Custom element that acts as a button to interact with the MCC search iframe.
 *
 * @constructor
 * Creates an instance of MCCSearchButton, sets up a reference to the iframe, and adds a click event listener.
 *
 * @method connectedCallback
 * Lifecycle method called when the element is added to the DOM. Ensures the button is a child of MCCSearch.
 *
 * @method handleClick
 * Handles the click event on the button. Sends a message to the iframe to open a modal.
 */
if (!customElements.get('mcc-search-button')) {
    class MCCSearchButton extends HTMLElement {
        constructor() {
            super();

            this.iframe = document.querySelector('#mcc-iframe');

            this.addEventListener('click', this.handleClick.bind(this));
            this.message = {
                type: 'openModal',
            };
        }

        connectedCallback() {
            if (!this.closest('mcc-search')) {
                console.error(
                    'MCCSearchButton must be a child of MCCSearch element.',
                );
            }
        }

        handleClick() {
            if (this.iframe) {
                this.iframe.style.display = 'block';

                // wait 100 milliseconds before sending the message to ensure the iframe is ready
                setTimeout(() => {
                    this.iframe.contentWindow.postMessage(this.message, '*');
                }, 100);
            }
        }
    }

    customElements.define('mcc-search-button', MCCSearchButton);

    if (!customElements.get('mcc-advanced-search')) {
        class MCCAdvancedSearch extends MCCSearchButton {
            constructor() {
                super();

                this.message = {
                    type: 'openModal',
                    window: 'advancedSearch',
                };
            }
        }

        customElements.define('mcc-advanced-search', MCCAdvancedSearch);
    }

    if (!customElements.get('mcc-saved-colors')) {
        class MCCSavedColorsButton extends MCCSearchButton {
            constructor() {
                super();

                this.message = {
                    type: 'openModal',
                    window: 'savedColors',
                };
            }
        }

        customElements.define('mcc-saved-colors', MCCSavedColorsButton);
    }
}

/**
 * @class MCCSelectedColor
 * @extends HTMLElement
 * @classdesc Custom element that listens for a message event to select a color.
 *
 * @constructor
 * Creates an instance of MCCSelectedColor and sets up an event listener for message events.
 *
 * @method handleMessage
 * Handles the message event to select a color.
 */
if (!customElements.get('mcc-selected-color')) {
    class MCCSelectedColor extends HTMLElement {
        constructor() {
            super();
            this.style.display = 'none';

            this.color = null;
            this.undercoat = null;

            this.priceGroupVariantName = this.getAttribute('price-code-variant-name');
            // TODO: get the index from the variant-selects element
            this.priceGroupVariantIndex = 1;

            // select the first variant-selects element on the page
            this.variantSelects.style.setProperty('display', 'none', 'important');

            // Add change event listener
            this.variantSelects.addEventListener('change', this.onVariantChange.bind(this));

            this.toggleAddButtonStatus(true, "");

            this.undercoatForm = this.querySelector('form#undercoatAddToCart');

            this.boundColorSelectedHandler = this.onColorSelected.bind(this);

            this.resetProductPrice();

            // console.log('Product form', this.productForm);
            // console.log('Product price', this.productPrice);
            // console.log('Section ID', this.sectionId);
            // console.log('Variant selects', this.variantSelects);
        }

        get productForm() {
            return document.querySelector('product-form form');
        }

        get variantSelects() {
            return document.querySelector('variant-selects');
        }

        get sectionId() {
            return this.productForm?.dataset.section || this.variantSelects?.dataset.section;
        }

        get productId() {
            return document.querySelector('product-info[data-product-id]')?.getAttribute('data-product-id');
        }

        get productPrice() {
            if (!this.sectionId) {
                return null;
            }

            return document.getElementById(
                `price-${this.sectionId}`,
            );
        }

        get newPriceItem() {
            if (!this.newProductPrice) {
                return null;
            }

            return this.newProductPrice.querySelector('.price .amount');
        }

        resetProductPrice() {
            if (!this.productPrice) {
                return;
            }

            // create a copy of product price without id and append next to product price
            this.newProductPrice = this.productPrice.cloneNode(true);
            this.newProductPrice.removeAttribute('id');

            this.productPrice.parentNode.appendChild(this.newProductPrice);

            // hide the original product price
            this.productPrice.style.display = 'none';
        }

        resetVariantSelects() {
            // console.log('Resetting variant selects');
            this.variantSelects.style.setProperty('display', 'block', 'important');

            // Hide the variant with the index priceGroupVariantIndex
            this.variantSelects.querySelectorAll('fieldset').forEach((item, index) => {
                if (index === this.priceGroupVariantIndex) {
                    item.style.setProperty('display', 'none', 'important');
                }
            });
        }

        connectedCallback() {
            this.colorCodeElement = this.querySelector('[data-color-code]');
            this.colorNameElement = this.querySelector('[data-color-name]');
            this.colorMakeElement = this.querySelector('[data-color-make]');
            this.colorYearElement = this.querySelector('[data-color-year]');
            this.colorImgElement = this.querySelector('[data-color-image]');
            this.colorPriceCodeElement =
                this.querySelector('[data-price-code]');
            this.colorUndercoatElement = this.querySelector('[data-undercoat]');
            this.colorUndercoatCodeElement = this.querySelector(
                '[data-undercoat-code]',
            );
            this.colorUndercoatNameElement = this.querySelector(
                '[data-undercoat-name]',
            );
            this.colorUndercoatImgElement = this.querySelector(
                '[data-undercoat-image]',
            );
            this.colorUndercoatPriceCodeElement = this.querySelector(
                '[data-undercoat-price-code]',
            );

            this.addEventListener(
                'colorSelected',
                this.boundColorSelectedHandler,
            );

            this.checkUrl();

            // to be deleted
            const selectBtn = this.querySelector("button");
            if (selectBtn) {
                // Add click
                selectBtn.addEventListener("click", this.selectColor.bind(this));
            }
        }

        disconnectedCallback() {
            clearInterval(this.interval);
        }

        checkUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            const colorBase64 = urlParams.get('color');

            if (!colorBase64) {
                return;
            }

            let detail = null;
            try {
                detail = JSON.parse(atob(colorBase64));
            } catch (e) {
                console.error('Error parsing color:', e);

                return;
            }

            if (detail) {
                this.dispatchEvent(
                    new CustomEvent('colorSelected', {
                        detail: detail,
                    }),
                );
              this.toggleAddButtonStatus(false, "");
            }
        }

        selectColor() {
            const color = {
                "code": "0172",
                "name": "VIOLA PARISEA P.",
                "brand": "LAMBORGHINI",
                "imgSrc": "url('http://spectro-images.e-mixing.eu/colors/14003359/1518076/637220436941275852.jpg')",
                "imgAlt": "VIOLA PARISEA P.",
                "year": "2017/",
                "tricoat": true,
                "candy": false,
                "xyralic": false,
                "silver": false,
                "priceCode": "E"
            }

            const undercoat = {
                "code": "U104",
                "name": "U104",
                "imgSrc": "url('http://spectro-images.e-mixing.eu/colors/141673/1501402/637221396214840543.jpg')",
                "imgAlt": "U104",
                "priceCode": "B"
            };

            this.dispatchEvent(
                new CustomEvent('colorSelected', {
                    detail: { color, undercoat },
                })
            )
        }

        onColorSelected(event) {
            const { color, undercoat } = event.detail;

            this.resetColorElementsStatus();

            this.color = color;
            this.undercoat = undercoat;

            // Json encode and base 64 encode the color and undercoat
            // const colorBase64 = btoa(JSON.stringify({color, undercoat}));
            // console.log('Color selected:', colorBase64);


            // console.log('Color selected:', color, undercoat);

            this.renderColorSelection();
            this.resetVariantSelects();
            this.addProductProperties();

            this.updateVariant();
        }

        renderColorSelection() {
            if (!this.color) {
                return;
            }

            if (this.colorCodeElement) {
                this.colorCodeElement.textContent = this.color.code;
            }

            if (this.colorNameElement) {
                this.colorNameElement.textContent = this.color.name;
            }

            if (this.colorMakeElement) {
                this.colorMakeElement.textContent = this.color.brand;
            }

            if (this.colorYearElement) {
                this.colorYearElement.textContent = this.color.year;
            }

            if (this.colorImgElement) {
                // set center center and cover background size
                this.colorImgElement.style.background =
                    this.color.imgSrc + ' center center / cover';
            }

            if (this.colorPriceCodeElement) {
                this.colorPriceCodeElement.textContent = this.color.priceCode;
            }

            this.updateUndercoatInput();
            this.renderUndercoatInfo();

            this.style.display = 'block';

            // scroll to the selected color -100px from the top
            const rect = this.getBoundingClientRect();
            const topPosition = rect.top + window.scrollY;
            window.scrollTo({
                top: topPosition - 300,
                behavior: 'smooth',
            });
        }

        updateVariant() {
            if (!this.color) {
                return;
            }

            // Select input with name priceGroupVariantName and value color.priceCode
            const priceGroupVariantInput = document.querySelector(
                `select[name="options[${this.priceGroupVariantName}]"]`,
            );

            // console.log('Price group variant input:', priceGroupVariantInput);

            // mark as checked
            if (priceGroupVariantInput) {
                // console.log('Price group variant input:', priceGroupVariantInput);
                priceGroupVariantInput.value = this.color.priceCode;

                //trigger change event
                // console.log('Dispatch Variants Change Event');
                priceGroupVariantInput.dispatchEvent(new Event('change', {bubbles: true}));
            }
        }

        addProductProperties() {
            if (!this.color || !this.productForm) {
                return;
            }

            // console.log('Adding product properties:', this.color);

            // Create shopify product properties for color { name, code, brand, year }
            const productProperties = {
                'Color Name': this.color.name,
                'Color Code': this.color.code,
                'Make': this.color.brand,
                'Year': this.color.year,
            };

            // add input fields to the form
            Object.entries(productProperties).forEach(([name, value]) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[${name}]`;
                input.value = value;

                this.productForm.appendChild(input);
            });
        }


        onVariantChange(event) {
            // console.log('Variant changed:', event);

            this.getVariantData();
            this.updateOptions();
            this.updateMasterId();
            this.updateUndercoatId();
            this.updateUndercoatInput();
            this.renderUndercoatInfo();
            this.updateProductPrice();

            // console.log('Variant data', this.getVariantData());
            // console.log('Variant options:', this.options);
            // console.log('Current variant', this.currentVariant);
            // console.log('Current undercoat', this.currentUndercoatVariant);
        }

        // Product.variants
        getVariantData() {
            this.variantData = this.variantData || OEM_CURRENT_PRODUCT_VARIANTS;
            return this.variantData;
        }

        updateProductPrice() {
            // console.log('Updating product price', this.currentVariant, this.newProductPrice);
            if (!this.newProductPrice || !this.currentVariant) {
                return;
            }

            let priceTotal = this.currentVariant.price;

            if (this.currentUndercoatVariant) {
                priceTotal += this.currentUndercoatVariant.price;
            }

            if (this.newPriceItem) {
                const format = window.theme.settings.money_with_currency_format || "${{amount}}";
                this.newPriceItem.textContent = formatMoney(priceTotal, format);
            }
        }

        updateOptions() {
            this.options = Array.from(
                this.variantSelects.querySelectorAll("select, fieldset"),
                (item) => {
                    if (item.tagName === "SELECT") {
                        return item.value;
                    } else {
                    return Array.from(item.querySelectorAll("input")).find(
                        (radio) => radio.checked,
                    )?.value;
                }
            }).filter(option => option !== undefined);
        }

        updateMasterId() {
            // console.log('Update master id', this.getVariantData());
            // console.log('Options', this.options);
            this.currentVariant = this.getVariantData().find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        return this.options[index] === option;
                })
                .includes(false);
            });
        }

        updateUndercoatId() {
            // check OEM_UNDERCOAT_VARIANTS is defined and is an object
            if (!OEM_UNDERCOAT_VARIANTS || typeof OEM_UNDERCOAT_VARIANTS !== 'object') {
                return;
            }

            if (!this.undercoat ) {
                this.currentUndercoatVariant = null;
                return;
            }

            // console.log('Undercoat variants', OEM_UNDERCOAT_VARIANTS);

            this.currentUndercoatVariant = OEM_UNDERCOAT_VARIANTS.find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        if (index === this.priceGroupVariantIndex) {
                            return this.undercoat.priceCode === option;
                        }

                        return this.options[index] === option;
                })
                .includes(false);
            });
        }

        renderUndercoatInfo() {
            if (!this.undercoat || !this.currentUndercoatVariant) {
                return;
            }


            if (this.colorUndercoatElement) {
                this.colorUndercoatElement.style.display = 'block';
            }

            if (this.colorUndercoatCodeElement) {
                this.colorUndercoatCodeElement.textContent = this.undercoat.code;
            }

            if (this.colorUndercoatNameElement) {
                this.colorUndercoatNameElement.textContent = this.undercoat.name;
            }

            if (this.colorUndercoatImgElement) {
                // set center center and cover background size
                this.colorUndercoatImgElement.style.background =
                    this.undercoat.imgSrc + ' center center / cover no-repeat';
            }

            if (this.colorUndercoatPriceCodeElement) {
                this.colorUndercoatPriceCodeElement.textContent =
                    this.undercoat.priceCode;
            }

            // Update price
            if (this.colorUndercoatElement.querySelector('.price-item')) {
                this.colorUndercoatElement.querySelector('.price-item').textContent = '+ ' +
                    formatPriceAmount(this.currentUndercoatVariant.price, 'USD');
            }
        }

        updateUndercoatInput() {
            if (!this.color || !this.undercoat || !this.undercoatForm || !this.currentUndercoatVariant) {
                return;
            }

            const undercoatInput = this.undercoatForm.querySelector('input[name=id]');
            if (undercoatInput) {
                undercoatInput.value = this.currentUndercoatVariant.id;
            }

            // Update properties[Undercoat Name]
            const undercoatNameInput = this.undercoatForm.querySelector('input[name="properties[Undercoat Name]"]');
            if (undercoatNameInput) {
                undercoatNameInput.value = this.undercoat.name;
            }

            // Update properties[Undercoat For]
            const undercoatForInput = this.undercoatForm.querySelector('input[name="properties[Undercoat For]"]');
            if (undercoatForInput) {
                undercoatForInput.value = `${this.color.name} / ${this.color.code}`;
            }
        }

        resetUndercoatInput() {
            if (!this.undercoatForm) {
                return;
            }

            const undercoatInput = this.undercoatForm.querySelector('input[name=id]');
            if (undercoatInput) {
                undercoatInput.value = '';
            }

            // Update properties[Undercoat Name]
            const undercoatNameInput = this.undercoatForm.querySelector('input[name="properties[Undercoat Name]"]');
            if (undercoatNameInput) {
                undercoatNameInput.value = '';
            }

            // Update properties[Undercoat For]
            const undercoatForInput = this.undercoatForm.querySelector('input[name="properties[Undercoat For]"]');
            if (undercoatForInput) {
                undercoatForInput.value = '';
            }
        }

        displayCartDrawer() {
            const cartDrawer = document.getElementById('Cart-Drawer');

            if (cartDrawer && !cartDrawer.hasAttribute('data-status-silence')) {
                cartDrawer.show();
            } else {
                return (window.location = window['routes']['cart_url']);
            }
        }

        toggleAddButtonStatus(disable = true, text) {
            if (!this.productForm) return;

            const addCartButtons = this.productForm.querySelectorAll(
              'button[type="submit"]',
            );
            const dynamicCheckout = this.productForm.querySelector(".dynamic-checkout");
            const shopifyCheckout = this.productForm.querySelector("shopify-accelerated-checkout");

            // console.log('Add cart buttons', addCartButtons);
            // console.log('Dynamic checkout', dynamicCheckout);
            // console.log('Shopify checkout', shopifyCheckout);

            if (disable) {
              addCartButtons.forEach((addButton) => {
                addButton.setAttribute("disabled", "disabled");
                if (!text) return;

                const addButtonTextElement = addButton.querySelector(".button-text");
                if (addButtonTextElement) addButtonTextElement.textContent = text;
              });

              // 隐藏动态支付
              if (dynamicCheckout) dynamicCheckout.style.display = "none !important";
              if (shopifyCheckout) shopifyCheckout.style.display = "none !important";
            } else {
              addCartButtons.forEach((button) => {
                button.removeAttribute("disabled");

                const addButtonTextElement = button.querySelector(".button-text");
                if (!addButtonTextElement) return;

                addButtonTextElement.textContent = text
                  ? text
                  : window["variantStrings"]["addToCart"];
              });

              // 显示动态结账
              if (dynamicCheckout) dynamicCheckout.style.display = "none !important";
              if (shopifyCheckout) shopifyCheckout.style.display = "none !important";
            }
        }

        resetColorElementsStatus() {
            if (this.colorCodeElement) {
                this.colorCodeElement.textContent = "";
            }

            if (this.colorNameElement) {
                this.colorNameElement.textContent = "";
            }

            if (this.colorMakeElement) {
                this.colorMakeElement.textContent = "";
            }

            if (this.colorYearElement) {
                this.colorYearElement.textContent = "";
            }

            if (this.colorImgElement) {
                this.colorImgElement.style.background = "";
            }

            if (this.colorPriceCodeElement) {
                this.colorPriceCodeElement.textContent = "";
            }

            if (this.colorUndercoatElement) {
                this.colorUndercoatElement.style.display = "none";
            }

            if (this.colorUndercoatCodeElement) {
                this.colorUndercoatCodeElement.textContent = "";
            }

            if (this.colorUndercoatNameElement) {
                this.colorUndercoatNameElement.textContent = "";
            }

            if (this.colorUndercoatImgElement) {
                this.colorUndercoatImgElement.style.background = "";
            }

            if (this.colorUndercoatPriceCodeElement) {
                this.colorUndercoatPriceCodeElement.textContent = "";
            }

            this.resetUndercoatInput();
        }
    }

    customElements.define('mcc-selected-color', MCCSelectedColor);
}

if (!customElements.get('mcc-search-input')) {
    class MCCSearchInput extends HTMLElement {
        constructor() {
            super();

            this.iframe = document.querySelector('#mcc-iframe');

            this.boundFormSubmitHandler = this.onFormSubmit.bind(this);
        }

        connectedCallback() {
            this.form = this.querySelector('form');
            if (this.form) {
                this.form.addEventListener(
                    'submit',
                    this.boundFormSubmitHandler,
                );
            }
        }

        disconnectedCallback() {
            if (this.form) {
                this.form.removeEventListener(
                    'submit',
                    this.boundFormSubmitHandler,
                );
            }
        }

        onFormSubmit(event) {
            event.preventDefault();
            const formData = new FormData(this.form);
            const searchQuery = formData.get('searchQuery');

            if (!searchQuery) {
                return;
            }

            const message = {
                type: 'openModal',
                window: 'colorSearch',
                searchQuery,
            };

            if (this.iframe) {
                this.iframe.style.display = 'block';

                // wait 100 milliseconds before sending the message to ensure the iframe is ready
                setTimeout(() => {
                    this.iframe.contentWindow.postMessage(message, '*');
                }, 100);
            }
        }
    }

    customElements.define('mcc-search-input', MCCSearchInput);
}



// Only set up the fetch interceptor once
if (!window.fetchInterceptorInitialized) {
    window.fetchInterceptorInitialized = true;

    const originalFetch = window.fetch;

    window.fetch = async function(...args) {
        const [url, config] = args;
        const productForm = document.querySelector('product-form form');

        if (url.includes(`/cart/add`)) {
            const undercoatProductForm = document.querySelector('#undercoatAddToCart');

            if (!undercoatProductForm || !productForm) {
                return originalFetch.apply(this, args);
            }

            // check undercoat product form has input[name="id"] and is not empty
            const undercoatIdInput = undercoatProductForm.querySelector('input[name="id"]');
            if (!undercoatIdInput || !undercoatIdInput.value) {
                return originalFetch.apply(this, args);
            }

            // const undercoatFormData = new FormData(undercoatProductForm);

            try {
                console.log('MCC: Intercepting cart addition:', args);

                const undercoatProperties = {
                    // 'Undercoat Name': undercoatForm.querySelector('input[name="properties[Undercoat Name]"]').value,
                    'Undercoat For': undercoatProductForm.querySelector('input[name="properties[Undercoat For]"]').value,
                };

                // Select quantity from product form
                const quantity = productForm.querySelector('input[name=quantity]').value || 1;

                const undercoatProduct = {
                    id: undercoatIdInput.value,
                    quantity: quantity,
                    properties: undercoatProperties,
                }

                // Add to cart as JSON
                const undercoatResponse = await originalFetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(undercoatProduct),
                });

                if (!undercoatResponse.ok) {
                    productForm.handleErrorMessage();
                }

                // First, add the original item
                const originalResponse = await originalFetch(url, config);
                const originalData = await originalResponse.clone().json();

                return originalResponse;
            } catch (error) {
                console.error('Error intercepting cart addition:', error);
                throw error;
            }
        }

        return originalFetch.apply(this, args);
    };
}
